import React, { useState, useEffect } from "react"
import { Container, Card, CardBody, Row, Col } from "reactstrap"
import { withTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import BootstrapTable from "react-bootstrap-table-next"
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit"
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator"
import { formatDate, IsPartnerCockpitModuleEnabled } from "helpers/misc_helpers"
import PartFilter from "components/Common/PartFilters"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useLoading } from "helpers/custom_hooks"
import { GetLOAs, GetPartnerCockpits } from "helpers/backend_helper"

const LOAList = props => {
  const [loas, setLoas] = useState([])
  const setLoading = useLoading()
  const [filteredLoas, setFilteredLoas] = useState([])
  const [selectedPartnerCockpit, setSelectedPartnerCockpit] = useState(null)
  const [partnerCockpits, setPartnerCockpits] = useState([])
  const [partnerCockpitEnabled, setPartnerCockpitEnabled] = useState(false)

  const loadPermissions = async _ => {
    const isEnabled = await IsPartnerCockpitModuleEnabled()
    setPartnerCockpitEnabled(isEnabled)
  }

  useEffect(() => {
    loadPermissions()
  }, [])

  // Handle partner cockpit filter change
  useEffect(() => {
    if (!selectedPartnerCockpit || selectedPartnerCockpit.value === "all") {
      setFilteredLoas(loas)
    } else {
      const filtered = loas.filter(
        loa => loa.partnerCockpitId === selectedPartnerCockpit.value
      )
      setFilteredLoas(filtered)
    }
  }, [selectedPartnerCockpit, loas])

  const loadData = async () => {
    setLoading(true)
    try {
      const [loasData, cockpitsData] = await Promise.all([
        GetLOAs(),
        GetPartnerCockpits(),
      ])
      setLoas(loasData)
      setPartnerCockpits(cockpitsData)
    } catch (error) {
      console.error("Error loading data:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const columns = [
    {
      dataField: "cpn",
      text: props.t("CPN"),
      sort: true,
    },
    {
      dataField: "mpn",
      text: props.t("Part no."),
      sort: true,
    },
    {
      dataField: "manufacturer",
      text: props.t("Manufacturer"),
      sort: true,
    },
    {
      dataField: "supplier",
      text: props.t("Supplier"),
      sort: true,
    },
    {
      dataField: "price",
      text: props.t("Price"),
      sort: true,
      formatter: (cell, row) => (
        <>{cell ? cell.toFixed(5) + " " + row.currency : "-"}</>
      ),
      headerStyle: _ => ({
        textAlign: "right",
      }),
      align: () => "right",
    },
    {
      dataField: "moq",
      text: props.t("MOQ"),
      sort: true,
      formatter: cell => <>{cell} pc.</>,
      headerStyle: _ => ({
        textAlign: "right",
      }),
      align: () => "right",
    },
    {
      dataField: "leadTime",
      text: props.t("Lead Time"),
      sort: true,
    },
    {
      dataField: "packagingUnit",
      text: props.t("Packaging Unit"),
      sort: true,
    },
    {
      dataField: "partnerCockpitName",
      text: props.t("Partner Cockpit"),
      sort: true,
    },
    {
      dataField: "createdAt",
      text: props.t("Created At"),
      sort: true,
      formatter: cell => formatDate(cell),
    },
    {
      dataField: "actions",
      text: props.t("Actions"),
      formatter: (cell, row) => (
        <Link
          to={`/partner-cockpit/loa-details/${row.id}`}
          className="btn btn-sm btn-primary"
        >
          {props.t("View Details")}
        </Link>
      ),
    },
  ]

  const pageOptions = {
    sizePerPage: 10,
    totalSize: filteredLoas.length,
    custom: true,
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {partnerCockpitEnabled ? (
            <>
              <Breadcrumbs
                title={props.t("Partner Cockpit")}
                breadcrumbItem={props.t("LOA List")}
              />
              <Card>
                <CardBody>
                  <PaginationProvider
                    pagination={paginationFactory(pageOptions)}
                    keyField="id"
                    columns={columns}
                    data={filteredLoas}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="id"
                        data={filteredLoas}
                        columns={columns}
                        bootstrap4
                        search
                      >
                        {toolkitProps => (
                          <React.Fragment>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h4 className="mb-0">{props.t("LOA List")}</h4>
                              <Link
                                to="/partner-cockpit/loa-upload"
                                className="btn btn-primary"
                              >
                                {props.t("Upload LOAs")}
                              </Link>
                            </div>

                            <PartFilter
                              isOpen={true}
                              parts={loas}
                              setParts={setFilteredLoas}
                              searchProps={toolkitProps.searchProps}
                              showPartnerCockpitFilter={true}
                              showCPNFilter={true}
                            />

                            <div className="table-responsive mt-4">
                              <BootstrapTable
                                {...toolkitProps.baseProps}
                                {...paginationTableProps}
                                bordered={false}
                                striped
                                hover
                                classes={"table align-middle table-nowrap"}
                                headerWrapperClasses={"table-light"}
                              />
                              {filteredLoas.length === 0 && (
                                <p className="text-center">
                                  {props.t("No LOAs available")}
                                </p>
                              )}
                            </div>
                            <div className="pagination pagination-rounded justify-content-end mb-2">
                              <PaginationListStandalone {...paginationProps} />
                            </div>
                          </React.Fragment>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </>
          ) : (
            <Row>
              <Col lg="12">
                <div className="text-center mb-5">
                  <h1 className="display-2 fw-medium">
                    4<i className="bx bx-buoy bx-spin text-primary display-3" />
                    3
                  </h1>
                  <h4 className="text-uppercase">
                    Sorry, you are not allowed to view this page
                  </h4>
                  <p className="text-primary">
                    This is a premium feature. To use this feature, please
                    upgrade your license!
                  </p>
                  <div className="mt-5 text-center">
                    <Link
                      className="btn btn-primary waves-effect waves-light "
                      to="/settings?tab=2"
                    >
                      Upgrade Account
                    </Link>
                    <br />
                    <Link className=" d-block mt-4" to="/dashboard">
                      <i className="bx bx-left-arrow-alt" />
                      Back to Dashboard
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(LOAList)
